import { getEffectiveSelectionRange } from "../selection-tracker";
import {
  getBool,
  getNumber,
  getParagraphAt,
  getString,
  toAlignment,
  toUnderline,
} from "./utils";

type FormatTarget = "selection" | "paragraph";

async function resolveFormatTarget(
  ctx: Word.RequestContext,
  args: Record<string, unknown>
): Promise<Word.Range | Word.Paragraph> {
  const target = getString(args, "target") as FormatTarget; // narrows string → union
  switch (target) {
    case "selection": {
      const effective = await getEffectiveSelectionRange(ctx);
      return effective ? effective.range : ctx.document.getSelection();
    }
    case "paragraph": {
      const index = getNumber(args, "paragraphIndex");
      return getParagraphAt(ctx, index);
    }
    default:
      throw new Error(
        `Invalid target "${String(target)}". Expected one of: selection, paragraph.`
      );
  }
}

export async function formatSelectionCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const target = await resolveFormatTarget(ctx, args);
  const font = target.font;
  const bold = getBool(args, "bold");
  if (bold !== undefined) {
    font.bold = bold;
  }
  const italic = getBool(args, "italic");
  if (italic !== undefined) {
    font.italic = italic;
  }
  if (typeof args.underline === "string") {
    font.underline = toUnderline(args.underline);
  }
  const name = getString(args, "name", false);
  if (name !== undefined) {
    font.name = name;
  }
  const size = getNumber(args, "size", false);
  if (size !== undefined) {
    font.size = size;
  }
  const color = getString(args, "color", false);
  if (color !== undefined) {
    font.color = color;
  }
  const highlightColor = getString(args, "highlightColor", false);
  if (highlightColor !== undefined) {
    font.highlightColor = highlightColor;
  }
  const strikethrough = getBool(args, "strikethrough");
  if (strikethrough !== undefined) {
    font.strikeThrough = strikethrough;
  }
  const superscript = getBool(args, "superscript");
  if (superscript !== undefined) {
    font.superscript = superscript;
  }
  const subscript = getBool(args, "subscript");
  if (subscript !== undefined) {
    font.subscript = subscript;
  }
  return { ok: true };
}

export async function setParagraphAlignmentCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const index = getNumber(args, "paragraphIndex");
  const paragraph = await getParagraphAt(ctx, index);
  paragraph.alignment = toAlignment(args.alignment);
  return { ok: true };
}

export async function setParagraphStyleCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const index = getNumber(args, "paragraphIndex");
  const styleBuiltIn = getString(args, "styleBuiltIn");
  const paragraph = await getParagraphAt(ctx, index);
  paragraph.styleBuiltIn = styleBuiltIn as Word.BuiltInStyleName;
  return { ok: true };
}

export async function setParagraphSpacingCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const index = getNumber(args, "paragraphIndex");
  const paragraph = await getParagraphAt(ctx, index);
  const leftIndent = getNumber(args, "leftIndent", false);
  if (leftIndent !== undefined) {
    paragraph.leftIndent = leftIndent;
  }
  const rightIndent = getNumber(args, "rightIndent", false);
  if (rightIndent !== undefined) {
    paragraph.rightIndent = rightIndent;
  }
  const firstLineIndent = getNumber(args, "firstLineIndent", false);
  if (firstLineIndent !== undefined) {
    paragraph.firstLineIndent = firstLineIndent;
  }
  const lineSpacing = getNumber(args, "lineSpacing", false);
  if (lineSpacing !== undefined) {
    paragraph.lineSpacing = lineSpacing;
  }
  const spaceBefore = getNumber(args, "spaceBefore", false);
  if (spaceBefore !== undefined) {
    paragraph.spaceBefore = spaceBefore;
  }
  const spaceAfter = getNumber(args, "spaceAfter", false);
  if (spaceAfter !== undefined) {
    paragraph.spaceAfter = spaceAfter;
  }
  return { ok: true };
}
